import { CheckCircleOutline } from '@mui/icons-material';
import { Button, IconButton, TableCell, TableRow, Tooltip } from '@mui/material';
import { UseFormReturn } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { selectToolOwnerTools } from '../../../../Auth/store/accountSlice';
import { EntityState } from '../../../../Common/api/models/EntityState';
import { ProjectTool, ToolStatusEnum } from '../../../../Common/api/models/ProjectTool';
import ToolCard from '../../../../Common/components/ToolCard/ToolCard';
import ToolDetailsFieldsTableRow from '../../../../Common/components/ToolDetailsFieldsTableRow/ToolDetailsFieldsTableRow';
import { ProjectFormType } from '../../../../Common/hooks/useCreateProject';
import { trackAppInsightsEvent } from '../../../../Common/services/applicationInsights';
import { ProjectFinalizeFormType } from '../../hooks/useProjectDetails';
import EntityDeleteToggleButton from '../ToolDeleteToggleButton/EntityDeleteToggleButton';
import ToolLaunchIcon from '../ToolLaunchIcon/ToolLaunchIcon';
import ToolStatusBadge from '../ToolStatus/ToolStatusBadge';
import ToolStatusChip from '../ToolStatusChip/ToolStatusChip';
import styles from './ProjectToolTableRow.module.scss';

interface ProjectToolTableRowProps {
    form: UseFormReturn<ProjectFormType>;
    toolOpen: boolean;
    toolIsHidden: boolean;
    tool: ProjectTool;
    toolInviteMode: string;
    toolIsMandatory: boolean;
    rowIndex: number;
    userCanEdit: boolean;
    canManageAzureResources?: boolean;
    canManageGithubRepositories?: boolean;
    userIsAdmin: boolean;
    handleDelete: (index: number) => void;
    ToggleItemButton: React.ReactNode;
    setIsFinalizeProjectPopupOpen: (isOpen: boolean) => void;
    finalizeForm: UseFormReturn<ProjectFinalizeFormType>;
    isLastItem?: boolean;
    useCustomRoleMapping: boolean;
    currentUserToolIdsWithAccess?: string[];
    isCurrentUserToolAccessLoading?: boolean;
}

const ProjectToolTableRow = ({
    form,
    toolOpen,
    tool,
    toolIsMandatory,
    toolInviteMode,
    rowIndex,
    userCanEdit,
    canManageAzureResources = false,
    canManageGithubRepositories = false,
    userIsAdmin,
    handleDelete,
    ToggleItemButton,
    setIsFinalizeProjectPopupOpen,
    finalizeForm,
    isLastItem,
    useCustomRoleMapping,
    currentUserToolIdsWithAccess = [],
    isCurrentUserToolAccessLoading = true,
}: ProjectToolTableRowProps): JSX.Element => {
    const { watch } = form;

    const ToolOwnerTools = useSelector(selectToolOwnerTools);
    const hasFinalizePermission = ToolOwnerTools.some((adminTool) =>
        (tool.capabilityToolKey.toLowerCase() ?? '').includes(adminTool.toLowerCase()),
    );

    const toolEditStatus = watch(`tools.${rowIndex}.editStatus`) as EntityState;
    const capabilityToolKey = String(tool.capabilityToolKey ?? '');
    const capabilityToolKeyParts = capabilityToolKey.split(':');
    const reversedCapabilityToolKey =
        capabilityToolKeyParts.length === 2
            ? `${capabilityToolKeyParts[1]}:${capabilityToolKeyParts[0]}`
            : capabilityToolKey;
    const hasAccess =
        currentUserToolIdsWithAccess.includes(capabilityToolKey) ||
        currentUserToolIdsWithAccess.includes(reversedCapabilityToolKey);
    const canRequestAccess = !isCurrentUserToolAccessLoading && !hasAccess;
    const isRequestAccessVisible = window.configuration?.environment !== 'prod';

    const openFinalizePopup = () => {
        finalizeForm.setValue('toolProjectDeeplink', '');
        finalizeForm.setValue('toolProjectId', '');
        finalizeForm.setValue('toolId', tool.toolId);
        finalizeForm.setValue('capabilityId', tool.capabilityId);
        setIsFinalizeProjectPopupOpen(true);
    };

    return (
        <>
            <TableRow>
                <TableCell colSpan={2}>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <ToolCard
                            capabilityName={tool.capabilityName}
                            toolKey={tool.capabilityToolKey}
                            toolName={tool.toolDisplayName ?? tool.toolName}
                            tooltip={tool.toolName}
                        />
                        <ToolStatusChip toolEditStatus={toolEditStatus} isNew={tool.isToolNew} />
                    </div>
                </TableCell>

                <TableCell>{toolInviteMode}</TableCell>
                <TableCell sx={{ p: 0, pl: 2, height: '100%' }}>
                    <div className={styles.toolDetails__collapseCell}>
                        <ToolStatusBadge status={tool.status} />
                        <div>
                            {(userIsAdmin || hasFinalizePermission) &&
                                tool.status == ToolStatusEnum.CreationPending && (
                                    <Tooltip title='Finalize tool creation' arrow placement='top'>
                                        <IconButton
                                            aria-label='finalize'
                                            onClick={openFinalizePopup}
                                        >
                                            <CheckCircleOutline />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            {canRequestAccess && isRequestAccessVisible ? (
                                <Tooltip title='Request access' arrow placement='top'>
                                    <Button
                                        size='small'
                                        variant='outlined'
                                        onClick={() =>
                                            trackAppInsightsEvent(
                                                'Project Tools Request Access Clicked',
                                                {
                                                    projectId: tool.projectId,
                                                    projectToolId: tool.toolId,
                                                    capabilityId: tool.capabilityId,
                                                    capabilityToolKey: tool.capabilityToolKey,
                                                    toolName: tool.toolName,
                                                    toolDisplayName: tool.toolDisplayName,
                                                },
                                            )
                                        }
                                        aria-label={`request access for ${tool.toolName}`}
                                        sx={{ ml: 0.5, textTransform: 'none' }}
                                    >
                                        Request access
                                    </Button>
                                </Tooltip>
                            ) : (
                                <ToolLaunchIcon toolProjectDeeplink={tool.toolProjectDeeplink} />
                            )}
                            {userCanEdit && !toolIsMandatory && (
                                <EntityDeleteToggleButton
                                    entityName={'tool'}
                                    entityStatus={toolEditStatus}
                                    onClick={() => handleDelete(rowIndex)}
                                    preventLastItemRemoval={true}
                                    isLastItem={isLastItem}
                                />
                            )}
                        </div>
                        {ToggleItemButton}
                    </div>
                </TableCell>
            </TableRow>
            <ToolDetailsFieldsTableRow
                form={form}
                index={rowIndex}
                toolKey={tool.capabilityToolKey}
                CustomFields={tool.CustomFields}
                toolOpen={toolOpen}
                userCanEdit={userCanEdit}
                canManageAzureResources={canManageAzureResources}
                canManageGithubRepositories={canManageGithubRepositories}
                status={tool.status}
                isEdit={tool.status !== ToolStatusEnum.New}
                useCustomRoleMapping={useCustomRoleMapping}
                projectId={tool.projectId}
                toolId={tool.toolId}
                toolProjectId={tool.toolProjectId}
            />
        </>
    );
};

export default ProjectToolTableRow;
